import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { GridRenderCellParams } from "@mui/x-data-grid-pro";
import { KeyboardEvent, useCallback, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuditSettings } from "../../../hooks/useAuditSettings";
import { useAuditTimeZone } from "../../../hooks/useAuditTimeZone";
import { AuditRow } from "../../../models/AuditRow";
import { RefuelEntry } from "../../../models/AuditTrip";
import { AUDIT_TOKENS } from "../../../theme/auditTokens";
import { formatNumber } from "../../../utils/format";
import { formatClockInZone, formatDayInZone } from "../../../utils/timeZone";
import { RefuelMap, RefuelPoint } from "../../map/RefuelMap";
import { rowAdBlueLiters, rowAdBlueRefuels, rowRefuelLiters, rowShownRefuels, ShownRefuel } from "../rowValues";
import { FUEL_TYPE_UNITS } from "@/constants/FuelTypeGroups";

interface Props extends GridRenderCellParams<AuditRow> {
  kind: RefuelEntry["kind"];
}

/** Lets the pointer travel from the cell into the popover without closing it. */
const CLOSE_DELAY_MS = 150;
const MAP_HEIGHT = 300;
const POPOVER_WIDTH = 540;

/**
 * The refuelled amount; hovering or focusing it opens a popover that names every transaction
 * behind the figure (fuel of a trip without a transaction: Geotab's detected fill-ups or, for
 * electric vehicles, charging sessions in kWh) and shows where they happened on a small map.
 */
export function RefuelCell({ row, kind }: Props) {
  const { t, i18n } = useTranslation();
  const timeZone = useAuditTimeZone();
  const language = i18n.language;
  const popoverId = useId();
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<number | undefined>(undefined);

  const show = useCallback(() => {
    window.clearTimeout(closeTimer.current);
    setOpen(true);
  }, []);
  const hideSoon = useCallback(() => {
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  }, []);
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape") setOpen(false);
  };

  const total = kind === "fuel" ? rowRefuelLiters(row) : rowAdBlueLiters(row);
  if (total == null) return null;
  const unit = kind === "fuel" && row.device.fuelType === "electric" ? FUEL_TYPE_UNITS.electric : "l";
  const when = (date: Date) => `${formatDayInZone(date, timeZone)} ${formatClockInZone(date, timeZone)}`;
  const transactionLine = (entry: RefuelEntry) => {
    const source =
      entry.source === "app"
        ? t("logbookAudit.source.appFuel")
        : t("logbookAudit.source.importFuel", { provider: entry.provider || t("logbookAudit.source.unknownProvider") });
    return `${when(entry.dateTime)} · ${formatNumber(entry.liters, language, 1)} l · ${source}`;
  };
  const line = (shown: ShownRefuel) => {
    if (shown.kind === "transaction") return transactionLine(shown.entry);
    const estimated = shown.event.estimated ? ` · ${t("logbookAudit.detected.estimated")}` : "";
    return `${when(shown.event.dateTime)} · ${formatNumber(shown.event.amount, language, 1)} ${unit} · ${t("logbookAudit.source.detectedShort")}${estimated}`;
  };
  const shown: ShownRefuel[] =
    kind === "fuel" ? rowShownRefuels(row) : rowAdBlueRefuels(row).map(entry => ({ kind: "transaction", entry }));
  const items = shown.map(item => {
    const location = item.kind === "transaction" ? item.entry.location : item.event.location;
    return { text: line(item), located: !!location, point: location ? ({ position: [location.y, location.x], label: line(item), detected: item.kind === "detected" } as RefuelPoint) : undefined };
  });
  const points = items.flatMap(item => (item.point ? [item.point] : []));

  return (
    <>
      <Box
        component="span"
        ref={anchorRef}
        tabIndex={0}
        aria-describedby={open ? popoverId : undefined}
        onMouseEnter={show}
        onMouseLeave={hideSoon}
        onFocus={show}
        onBlur={hideSoon}
        onKeyDown={onKeyDown}
        sx={{ fontVariantNumeric: "tabular-nums", borderRadius: "4px", "&:focus-visible": { outline: AUDIT_TOKENS.focusRing, outlineOffset: 2 } }}
      >
        {`${formatNumber(total, language, 1)} ${unit}`}
      </Box>
      <Popper id={popoverId} open={open} anchorEl={anchorRef.current} placement="bottom-end" sx={{ zIndex: 1500 }} role="tooltip">
        <Paper
          onMouseEnter={show}
          onMouseLeave={hideSoon}
          onFocus={show}
          onBlur={hideSoon}
          sx={{ width: POPOVER_WIDTH, p: "10px 12px", mt: "4px", fontFamily: AUDIT_TOKENS.fontFamily, fontSize: 12.5, lineHeight: 1.45, color: AUDIT_TOKENS.text, borderRadius: `${AUDIT_TOKENS.radius.card}px`, border: `1px solid ${AUDIT_TOKENS.line}`, boxShadow: AUDIT_TOKENS.shadow.panel }}
        >
          <Box component="ul" sx={{ m: "0 0 8px", pl: "16px" }}>
            {items.map(item => (
              <li key={item.text}>
                {item.text}
                {!item.located && <Box component="span" sx={{ color: AUDIT_TOKENS.muted }}>{` · ${t("logbookAudit.refuelMap.noLocation")}`}</Box>}
              </li>
            ))}
          </Box>
          {points.length > 0 && <RefuelMapWithTiles points={points} />}
        </Paper>
      </Popper>
    </>
  );
}

/**
 * The map with a switch between the user's regular tile layer (as picked for the trip map) and
 * satellite imagery; only mounted while the popover is open.
 */
function RefuelMapWithTiles({ points }: { points: RefuelPoint[] }) {
  const { t } = useTranslation();
  const { tileId } = useAuditSettings();
  const [satellite, setSatellite] = useState(false);
  return (
    <>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={satellite ? "satellite" : "map"}
        onChange={(_, next: "map" | "satellite" | null) => next && setSatellite(next === "satellite")}
        aria-label={t("logbookAudit.map.tileSwitch")}
        sx={{ mb: "8px" }}
      >
        <ToggleButton value="map" sx={{ textTransform: "none", fontSize: 12.5, px: 1.5, py: 0.25 }}>{t("logbookAudit.refuelMap.map")}</ToggleButton>
        <ToggleButton value="satellite" sx={{ textTransform: "none", fontSize: 12.5, px: 1.5, py: 0.25 }}>{t("logbookAudit.map.tiles.satellite")}</ToggleButton>
      </ToggleButtonGroup>
      <RefuelMap points={points} tileId={satellite ? "satellite" : tileId} height={MAP_HEIGHT} />
    </>
  );
}
